<script lang="ts">
	import type { ClassValue, HTMLInputAttributes } from "svelte/elements";
	import { Locale, type LocalizedString } from "@core/schema/Locale.js";
	import { emailPattern } from "@core/utils/email.js";
	import { getGlobalTemplateProps } from "../../contexts/getGlobalTemplateProps.js";

	const { wrapperClass, ...rest }: HTMLInputAttributes & { wrapperClass?: ClassValue } = $props();
	const { tenant } = getGlobalTemplateProps();

	let errorMessage = $state("");

	const type = $derived(rest.type ?? "text");
	const isCheckboxOrRadio = $derived(["radio", "checkbox"].includes(type));
	const customPattern: Partial<Record<NonNullable<HTMLInputAttributes["type"]>, string>> = {
		email: emailPattern,
	};
	const pattern = $derived(rest.pattern ?? customPattern[type]);

	const customValidationMessage: Partial<Record<NonNullable<HTMLInputAttributes["type"]>, LocalizedString>> = {
		email: {
			[Locale.cs]: "Zadali jste neplatnou e-mailovou adresu",
			[Locale.en]: "You have entered an invalid email address",
			[Locale.sk]: "Zadali ste neplatnú e-mailovú adresu",
		},
		url: {
			[Locale.cs]: "URL adresa (včetně https://)",
			[Locale.en]: "URL address (including https://)",
			[Locale.sk]: "URL adresa (vrátane https://)",
		},
		checkbox: {
			[Locale.cs]: "Toto pole je povinné",
			[Locale.en]: "This field is required",
			[Locale.sk]: "Toto pole je povinné",
		},
	};

	function validate({ currentTarget }: Event & { currentTarget: EventTarget & HTMLInputElement }): void {
		currentTarget.setCustomValidity("");

		if (currentTarget.validity.valid) {
			currentTarget.setCustomValidity("");
			errorMessage = "";
		} else {
			const customMessage = customValidationMessage[type]?.[tenant.locale];
			if (customMessage) {
				currentTarget.setCustomValidity(customMessage);
			}
			errorMessage = currentTarget.validationMessage;
		}
	}
</script>

{#if isCheckboxOrRadio}
	<input
		{...rest}
		class={[
			"min-w-0 border border-[#D7D7D7] rounded-[.25rem] [&:user-invalid]:border-red-300 [&:user-invalid]:outline-red-300 transition-[outline,text,border] duration-300 focus:border-zms-blue focus:[&:user-invalid]:border-red-300 focus:[&:user-invalid]:outline-red-300 outline-transparent outline-1 outline outline-offset-1 font-semibold md:text-[1.125rem]/[1.2] p-2 md:p-7 focus:text-zms-optical-black text-[#595959]",
			wrapperClass,
			rest.class,
		]}
		{pattern}
		oninput={(event: Event & { currentTarget: EventTarget & HTMLInputElement }): void => {
			validate(event);
			rest.oninput?.(event);
		}}
		oninvalid={(event: Event & { currentTarget: EventTarget & HTMLInputElement }): void => {
			event.preventDefault();
			validate(event);
			rest.oninvalid?.(event);
		}}
	/>
	{#if errorMessage}
		<span class="text-red-500 text-xs absolute -bottom-5 left-0 whitespace-nowrap">{errorMessage}</span>
	{/if}
{:else}
	<div class={["flex flex-col w-full", wrapperClass]}>
		<input
			{...rest}
			class={[
				"min-w-0 border border-[#D7D7D7] rounded-[.25rem] [&:user-invalid]:border-red-300 [&:user-invalid]:outline-red-300 transition-[outline,text,border] duration-300 focus:border-zms-blue focus:[&:user-invalid]:border-red-300 focus:[&:user-invalid]:outline-red-300 outline-transparent outline-1 outline outline-offset-1 font-semibold md:text-[1.125rem]/[1.2] p-2 md:p-7 focus:text-zms-optical-black text-[#595959] min-h-[3.125rem]",
				rest.class,
			]}
			{pattern}
			oninput={(event: Event & { currentTarget: EventTarget & HTMLInputElement }): void => {
				validate(event);
				rest.oninput?.(event);
			}}
			oninvalid={(event: Event & { currentTarget: EventTarget & HTMLInputElement }): void => {
				event.preventDefault();
				validate(event);
				rest.oninvalid?.(event);
			}}
		/>
		{#if errorMessage}
			<span class="text-red-500 text-xs mt-1">{errorMessage}</span>
		{/if}
	</div>
{/if}
